import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

const VIEW = 280;
const OUTPUT = 512;
const CROP_SESSION_KEY = "adatis.professionalPhotoCrop.session.v2";
const CROP_SESSION_LEGACY_KEY = "adatis.professionalPhotoCrop.session";
export const PHOTO_CROP_MEMORY_NEW_KEY = "new";

type CropSessionMemory = {
  relativeZoom: number;
  offsetNormX: number;
  offsetNormY: number;
};

type CropSessionMap = Record<string, CropSessionMemory>;

type ProfessionalPhotoCropModalProps = {
  isOpen: boolean;
  sourceFile: File | null;
  /** sessionStorage bucket — professional id or `new` for create. */
  memoryKey?: string;
  onCancel: () => void;
  onConfirm: (file: File) => void;
};

type PointerPoint = { x: number; y: number };

const isCropSessionMemory = (value: unknown): value is CropSessionMemory => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const parsed = value as CropSessionMemory;
  return (
    typeof parsed.relativeZoom === "number" &&
    typeof parsed.offsetNormX === "number" &&
    typeof parsed.offsetNormY === "number"
  );
};

const readCropSessionMap = (): CropSessionMap => {
  try {
    const raw = sessionStorage.getItem(CROP_SESSION_KEY);
    if (!raw) {
      const legacy = sessionStorage.getItem(CROP_SESSION_LEGACY_KEY);
      if (!legacy) {
        return {};
      }
      const parsed = JSON.parse(legacy) as unknown;
      if (isCropSessionMemory(parsed)) {
        const migrated = { [PHOTO_CROP_MEMORY_NEW_KEY]: parsed };
        sessionStorage.setItem(CROP_SESSION_KEY, JSON.stringify(migrated));
        sessionStorage.removeItem(CROP_SESSION_LEGACY_KEY);
        return migrated;
      }
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (isCropSessionMemory(parsed)) {
      return { [PHOTO_CROP_MEMORY_NEW_KEY]: parsed };
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const map: CropSessionMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (isCropSessionMemory(value)) {
        map[key] = value;
      }
    }
    return map;
  } catch {
    return {};
  }
};

const readCropSession = (memoryKey: string): CropSessionMemory | null => {
  const key = memoryKey.trim() || PHOTO_CROP_MEMORY_NEW_KEY;
  return readCropSessionMap()[key] ?? null;
};

const writeCropSession = (
  memoryKey: string,
  memory: CropSessionMemory
): void => {
  try {
    const key = memoryKey.trim() || PHOTO_CROP_MEMORY_NEW_KEY;
    const map = readCropSessionMap();
    map[key] = memory;
    sessionStorage.setItem(CROP_SESSION_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
};

const clearCropSession = (memoryKey: string): void => {
  try {
    const key = memoryKey.trim() || PHOTO_CROP_MEMORY_NEW_KEY;
    const map = readCropSessionMap();
    if (!(key in map)) {
      return;
    }
    delete map[key];
    if (Object.keys(map).length === 0) {
      sessionStorage.removeItem(CROP_SESSION_KEY);
      return;
    }
    sessionStorage.setItem(CROP_SESSION_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};

/** Drag / pinch the image inside a square frame, then export a 1:1 JPEG. */
const ProfessionalPhotoCropModal: React.FC<ProfessionalPhotoCropModalProps> = ({
  isOpen,
  sourceFile,
  memoryKey = PHOTO_CROP_MEMORY_NEW_KEY,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const dragOrigin = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const pointers = useRef<Map<number, PointerPoint>>(new Map());
  const pinchOrigin = useRef<{
    distance: number;
    zoom: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const offsetRef = useRef(offset);
  const zoomRef = useRef(zoom);

  offsetRef.current = offset;
  zoomRef.current = zoom;

  useEffect(() => {
    if (!sourceFile || !isOpen) {
      setObjectUrl(null);
      return;
    }

    const url = URL.createObjectURL(sourceFile);
    setObjectUrl(url);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    pointers.current.clear();
    pinchOrigin.current = null;

    const image = new Image();
    image.onload = () => {
      setNatural({ w: image.naturalWidth, h: image.naturalHeight });
      const cover = Math.max(
        VIEW / image.naturalWidth,
        VIEW / image.naturalHeight
      );
      const remembered = readCropSession(memoryKey);
      const nextZoom = remembered
        ? Math.min(cover * 3, Math.max(cover, cover * remembered.relativeZoom))
        : cover;
      const drawnW = image.naturalWidth * nextZoom;
      const drawnH = image.naturalHeight * nextZoom;
      const minX = Math.min(0, VIEW - drawnW);
      const minY = Math.min(0, VIEW - drawnH);
      const maxX = Math.max(0, VIEW - drawnW);
      const maxY = Math.max(0, VIEW - drawnH);
      const nextOffset = remembered
        ? {
            x: Math.min(maxX, Math.max(minX, remembered.offsetNormX * VIEW)),
            y: Math.min(maxY, Math.max(minY, remembered.offsetNormY * VIEW)),
          }
        : {
            x: (VIEW - drawnW) / 2,
            y: (VIEW - drawnH) / 2,
          };
      setZoom(nextZoom);
      setOffset(nextOffset);
    };
    image.src = url;

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [sourceFile, isOpen, memoryKey]);

  const minZoom =
    natural.w > 0
      ? Math.max(VIEW / natural.w, VIEW / natural.h)
      : 1;

  const clampOffset = (next: { x: number; y: number }, scale: number) => {
    const drawnW = natural.w * scale;
    const drawnH = natural.h * scale;
    const minX = Math.min(0, VIEW - drawnW);
    const minY = Math.min(0, VIEW - drawnH);
    const maxX = Math.max(0, VIEW - drawnW);
    const maxY = Math.max(0, VIEW - drawnH);

    return {
      x: Math.min(maxX, Math.max(minX, next.x)),
      y: Math.min(maxY, Math.max(minY, next.y)),
    };
  };

  const applyZoomAroundCenter = (nextZoom: number) => {
    const clamped = Math.min(minZoom * 3, Math.max(minZoom, nextZoom));
    const centerX = VIEW / 2;
    const centerY = VIEW / 2;
    const current = zoomRef.current;
    const relX = (centerX - offsetRef.current.x) / current;
    const relY = (centerY - offsetRef.current.y) / current;
    const nextOffset = clampOffset(
      {
        x: centerX - relX * clamped,
        y: centerY - relY * clamped,
      },
      clamped
    );
    setZoom(clamped);
    setOffset(nextOffset);
  };

  const pointerDistance = () => {
    const points = Array.from(pointers.current.values());
    if (points.length < 2) {
      return 0;
    }
    const dx = points[0].x - points[1].x;
    const dy = points[0].y - points[1].y;
    return Math.hypot(dx, dy);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    if (pointers.current.size === 2) {
      setDragging(false);
      pinchOrigin.current = {
        distance: pointerDistance(),
        zoom: zoomRef.current,
        offsetX: offsetRef.current.x,
        offsetY: offsetRef.current.y,
      };
      return;
    }

    setDragging(true);
    dragOrigin.current = {
      x: event.clientX,
      y: event.clientY,
      ox: offsetRef.current.x,
      oy: offsetRef.current.y,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) {
      return;
    }

    pointers.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    if (pointers.current.size >= 2 && pinchOrigin.current) {
      const distance = pointerDistance();
      if (pinchOrigin.current.distance > 0 && distance > 0) {
        applyZoomAroundCenter(
          pinchOrigin.current.zoom * (distance / pinchOrigin.current.distance)
        );
      }
      return;
    }

    if (!dragging) {
      return;
    }

    const dx = event.clientX - dragOrigin.current.x;
    const dy = event.clientY - dragOrigin.current.y;
    setOffset(
      clampOffset(
        {
          x: dragOrigin.current.ox + dx,
          y: dragOrigin.current.oy + dy,
        },
        zoomRef.current
      )
    );
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pointers.current.delete(event.pointerId);

    if (pointers.current.size < 2) {
      pinchOrigin.current = null;
    }

    if (pointers.current.size === 1) {
      const remaining = Array.from(pointers.current.values())[0];
      setDragging(true);
      dragOrigin.current = {
        x: remaining.x,
        y: remaining.y,
        ox: offsetRef.current.x,
        oy: offsetRef.current.y,
      };
      return;
    }

    setDragging(false);
  };

  const handleZoom = (value: number) => {
    applyZoomAroundCenter(value);
  };

  const persistCropSession = () => {
    if (natural.w === 0) {
      return;
    }
    const cover = Math.max(VIEW / natural.w, VIEW / natural.h);
    writeCropSession(memoryKey, {
      relativeZoom: zoomRef.current / cover,
      offsetNormX: offsetRef.current.x / VIEW,
      offsetNormY: offsetRef.current.y / VIEW,
    });
  };

  const resetFraming = () => {
    if (natural.w === 0) {
      return;
    }
    const cover = Math.max(VIEW / natural.w, VIEW / natural.h);
    const drawnW = natural.w * cover;
    const drawnH = natural.h * cover;
    setZoom(cover);
    setOffset({
      x: (VIEW - drawnW) / 2,
      y: (VIEW - drawnH) / 2,
    });
    clearCropSession(memoryKey);
  };

  const handleConfirm = async () => {
    if (!objectUrl || natural.w === 0) {
      return;
    }

    setBusy(true);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error("load"));
        el.src = objectUrl;
      });

      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT;
      canvas.height = OUTPUT;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("canvas");
      }

      const scale = OUTPUT / VIEW;
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, OUTPUT, OUTPUT);
      context.drawImage(
        image,
        offset.x * scale,
        offset.y * scale,
        natural.w * zoom * scale,
        natural.h * zoom * scale
      );

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (result) => (result ? resolve(result) : reject(new Error("blob"))),
          "image/jpeg",
          0.9
        );
      });

      const baseName = sourceFile?.name.replace(/\.[^.]+$/, "") || "foto";
      persistCropSession();
      onConfirm(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
    } catch {
      onCancel();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onCancel} centered>
      <ModalHeader toggle={onCancel}>
        {t("professionals.fields.photo.cropTitle")}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small">
          {t("professionals.fields.photo.cropHint")}
        </p>
        <div
          className="mx-auto mb-3 border border-2 border-primary rounded overflow-hidden position-relative bg-light"
          style={{
            width: VIEW,
            height: VIEW,
            cursor: dragging ? "grabbing" : "grab",
            touchAction: "none",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(event) => {
            onPointerUp(event);
            persistCropSession();
          }}
          onPointerCancel={(event) => {
            onPointerUp(event);
            persistCropSession();
          }}
          onWheel={(event) => {
            event.preventDefault();
            const step = minZoom * 0.12;
            applyZoomAroundCenter(
              zoomRef.current + (event.deltaY < 0 ? step : -step)
            );
            persistCropSession();
          }}
        >
          {objectUrl ? (
            <img
              src={objectUrl}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: offset.x,
                top: offset.y,
                width: natural.w * zoom,
                height: natural.h * zoom,
                maxWidth: "none",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          ) : null}
        </div>
        <Label htmlFor="photo-crop-zoom" className="small">
          {t("professionals.fields.photo.cropZoom")}
        </Label>
        <Input
          id="photo-crop-zoom"
          type="range"
          min={minZoom}
          max={minZoom * 3}
          step={0.01}
          value={zoom}
          onChange={(event) => handleZoom(Number(event.target.value))}
        />
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <Button
          color="link"
          type="button"
          className="ps-0"
          onClick={resetFraming}
          disabled={busy || natural.w === 0}
        >
          {t("professionals.fields.photo.cropReset")}
        </Button>
        <div className="d-flex gap-2">
          <Button color="light" type="button" onClick={onCancel} disabled={busy}>
            {t("professionals.actions.cancel")}
          </Button>
          <Button
            color="primary"
            type="button"
            onClick={() => void handleConfirm()}
            disabled={busy || !objectUrl}
          >
            {busy ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t("professionals.fields.photo.cropping")}
              </>
            ) : (
              t("professionals.fields.photo.cropConfirm")
            )}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ProfessionalPhotoCropModal;
